import React from "react";
import { useSelector } from "react-redux";
import clsx from "clsx";
import PropTypes from "prop-types";

// Material
import { makeStyles } from "@material-ui/core/styles";

// Config
import colors from "../../config/colors";
import useTranslate from "../../hooks/useTranslate";

// Components
import Avatar from "../Avatar";
import ContributorStatusChip from "../ContributorStatusChip";
import Text from "../Text";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    color: colors.cloudBurst
  },
  avatar: {
    height: 38,
    width: 38,
    marginRight: theme.spacing(1.5)
  },
  avatarIntel: {
    backgroundColor: colors.royalBlue
  },
  username: {
    marginBottom: theme.spacing(1)
  },
  usernameIntel: {
    color: colors.deepKoamaru
  },
  userTypeWrapper: {
    display: "flex",
    gap: 8
  },
  kulpai: {
    color: colors.royalBlue
  },
  italic: {
    fontStyle: "italic"
  }
}));

const ContributorItem = ({ boldName, className, hidePending, contributor }) => {
  const { data } = useSelector(s => s.caseDetail);
  const classes = useStyles();
  const getText = useTranslate();

  const isAnonymous = data.isIntel && contributor.isOwner;
  const renderName = isAnonymous
    ? getText("common.intelligence.anonymous")
    : contributor.fullName;

  const displayName =
    boldName || isAnonymous ? <b key={0}>{renderName}</b> : renderName;

  return (
    <div className={clsx(classes.root, className)}>
      <Avatar
        className={clsx(classes.avatar, {
          [classes.avatarIntel]: isAnonymous
        })}
        src={!isAnonymous && contributor.profilePictureURL}
        backgroundColor={contributor.colorHex}
      >
        {renderName.charAt(0)}
      </Avatar>
      <div className={classes.content}>
        <Text
          className={clsx(classes.username, {
            [classes.usernameIntel]: isAnonymous
          })}
          type="contentLabel2"
        >
          {contributor.isOwner ? (
            getText("common.valueLabels.usernameOwner", {
              isComponent: boldName || isAnonymous,
              1: displayName
            })
          ) : (
            <>
              {displayName}{" "}
              {!hidePending && !isAnonymous && (
                <ContributorStatusChip
                  joined={contributor.invitationAccepted}
                />
              )}
            </>
          )}
        </Text>
        <div className={classes.userTypeWrapper}>
          <Text
            text={`common.contributorCapacity.${contributor.capacity}`}
            type="contentLabel3"
          />
          {isAnonymous && (
            <Text type="contentLabel3">
              <span className={classes.kulpai}>
                {`${getText("common.intelligence.kulpai.part1")}`}{" "}
                <span className={classes.italic}>
                  {getText("common.intelligence.kulpai.part2")}
                </span>
              </span>
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};

ContributorItem.propTypes = {
  className: PropTypes.string,
  boldName: PropTypes.bool,
  hidePending: PropTypes.bool,
  contributor: PropTypes.PropTypes.shape({
    capacity: PropTypes.string,
    fullName: PropTypes.string,
    id: PropTypes.string,
    invitationAccepted: PropTypes.bool,
    isOwner: PropTypes.bool,
    profilePictureURL: PropTypes.string,
    colorHex: PropTypes.string
  }).isRequired
};

ContributorItem.defaultProps = {
  className: "",
  boldName: false,
  hidePending: false
};

export default ContributorItem;
